import { useLang } from "@/context/LanguageContext";
import { fmtDate, ACTION_STATUS } from "@/lib/format";
import { CheckCircle2, XCircle, RotateCw } from "lucide-react";

// Rich, read-only workflow step view: per-step status, timings, granular green/red item
// chips, and the original onboarding configuration summary. Shared by Jobs + Action Log so
// the full detail stays visible even after a workflow has finished.
export const WorkflowSteps = ({ wf, isAdmin = false, onRetryStep, busy = "" }) => {
  const { t } = useLang();
  return (
    <>
      <div className="space-y-2" data-testid={`wf-steps-${wf.id}`}>
        {wf.steps.map((st, i) => {
          const dur = st.dispatchedAt && st.finishedAt
            ? Math.max(0, Math.round((new Date(st.finishedAt) - new Date(st.dispatchedAt)) / 1000)) : null;
          const failedItems = (st.detail || []).filter((d) => d && d.ok === false);
          const okItems = (st.detail || []).filter((d) => d && d.ok === true);
          const isCurrent = wf.status === "running" && i === wf.currentStep;
          return (
            <div key={st.key + i} className={`rounded-sm border px-3 py-2 ${st.status === "error" ? "border-red-200 bg-red-50" : st.status === "skipped" ? "border-amber-200 bg-amber-50/50" : st.status === "done" ? "border-emerald-200 bg-emerald-50/40" : isCurrent ? "border-primary/40 bg-primary/5" : "border-zinc-200"}`} data-testid={`wf-step-${wf.id}-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm text-zinc-700">
                  <span className="font-mono text-[10px] text-zinc-400">{String(i + 1).padStart(2, "0")}</span>
                  {st.label}
                </span>
                <span className={`shrink-0 border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ACTION_STATUS[st.status] || ""}`}>{st.status}</span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-[10px] text-zinc-400">
                {st.kind === "verify" && st.attempts > 0 && <span>attempt {st.attempts}</span>}
                {st.dispatchedAt && <span>start {fmtDate(st.dispatchedAt)}</span>}
                {st.finishedAt && <span>end {fmtDate(st.finishedAt)}</span>}
                {dur !== null && <span>· {dur}s</span>}
              </div>
              {st.message && <div className="mt-1 font-mono text-[11px] text-zinc-500">{st.message}</div>}
              {(okItems.length > 0 || failedItems.length > 0) && (
                <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`wf-step-detail-${wf.id}-${i}`}>
                  {okItems.map((d, k) => (
                    <span key={`ok${k}`} title={`${d.type} ${d.id || d.target || ""}`} className="inline-flex max-w-full items-center gap-1 rounded-sm border border-emerald-200 bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-900" data-testid={`wf-detail-ok-${wf.id}-${i}-${k}`}>
                      <CheckCircle2 size={10} className="shrink-0" /> <span className="truncate">{d.label || d.type || d.id}</span>
                    </span>
                  ))}
                  {failedItems.map((d, k) => (
                    <span key={`f${k}`} title={d.error || ""} className="inline-flex max-w-full items-center gap-1 rounded-sm border border-red-200 bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-900" data-testid={`wf-detail-fail-${wf.id}-${i}-${k}`}>
                      <XCircle size={10} className="shrink-0" /> <span className="truncate">{d.label || d.type || d.id}{d.error ? ` — ${d.error}` : ""}</span>
                    </span>
                  ))}
                </div>
              )}
              {isAdmin && onRetryStep && (st.status === "error" || st.status === "skipped") && (
                <button onClick={() => onRetryStep(wf.id, i)} disabled={busy === wf.id + i} data-testid={`wf-step-retry-${wf.id}-${i}`} className="mt-2 inline-flex items-center gap-1 rounded-sm border border-primary/40 px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/5 disabled:opacity-50">
                  <RotateCw size={12} /> {st.status === "skipped" ? t("jobs.runThisStep") : t("jobs.retryThisStep")}
                </button>
              )}
            </div>
          );
        })}
      </div>
      {wf.configSummary && Object.keys(wf.configSummary).length > 0 && (
        <div className="mt-2 space-y-3 border-t border-zinc-100 pt-4" data-testid={`wf-config-${wf.id}`}>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">{t("jobs.configTitle")}</div>
          {wf.configSummary.identity && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-3">
              {Object.entries(wf.configSummary.identity).filter(([, v]) => v).map(([k, v]) => (
                <div key={k}><span className="text-zinc-400">{k}: </span><span className="text-zinc-700">{v}</span></div>
              ))}
            </div>
          )}
          {[["groups", t("onb.sumGroups")], ["dls", t("onb.sumDistribution")], ["teams", t("onb.sumTeams")], ["licenses", t("onb.sumLicenses")], ["shared", t("onb.sumShared")]].map(([key, label]) => {
            const items = wf.configSummary[key] || [];
            if (!items.length) return null;
            return (
              <div key={key} className="text-xs">
                <span className="font-semibold text-zinc-600">{label} ({items.length}): </span>
                <span className="text-zinc-500">{items.map((x) => (typeof x === "string" ? x : x.name || x.displayName || x.mailbox || JSON.stringify(x))).join(", ")}</span>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};
